/**
 * A tooltip with room to say something worth reading.
 *
 * Three slots, in the order an operator needs them. The eyebrow names what
 * kind of thing the control is, so a row of similar-looking chips can be told
 * apart without reading every sentence. The body is the sentence. The hint row
 * carries the secondary fact — what it costs, what it will not do, which key
 * does it — kept out of the body so the body stays one thought long.
 *
 * Only the body is required, and a control with nothing but a name to give
 * should keep using a plain Tooltip rather than dressing up two words.
 *
 * The card is dark in both themes. In a light theme that is the ordinary
 * inverted-tooltip convention; in a dark one it stops a hover from flashing a
 * white slab over the map, which is what the default did here.
 */
import React from 'react';
import { Box, Tooltip, TooltipProps } from '@mui/material';

const SURFACE = '#1b1e26';
const BORDER = 'rgba(255,255,255,0.13)';
const BODY_TEXT = '#e9ecf2';
const HINT_TEXT = '#98a2b8';
const EYEBROW_TONE = '#f5b942';

export interface RichTooltipProps extends Omit<TooltipProps, 'title'> {
  /** Small-caps lead-in naming the kind of control this is. */
  eyebrow?: string;
  /** Accent for the eyebrow. Reach for it when the kind carries a warning. */
  eyebrowTone?: string;
  /** The sentence that earns the hover. */
  body: React.ReactNode;
  /** Secondary row: the cost, the caveat, the binding. */
  hint?: React.ReactNode;
  /** Keys drawn as caps in the hint row. Real bindings only. */
  keys?: string[];
}

const Key: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 16,
      height: 16,
      px: 0.4,
      borderRadius: 0.5,
      border: `1px solid ${BORDER}`,
      bgcolor: 'rgba(255,255,255,0.06)',
      color: BODY_TEXT,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '0.58rem',
      fontWeight: 700,
      lineHeight: 1,
    }}
  >
    {children}
  </Box>
);

const RichTooltip: React.FC<RichTooltipProps> = ({
  eyebrow,
  eyebrowTone = EYEBROW_TONE,
  body,
  hint,
  keys,
  children,
  placement = 'top',
  ...rest
}) => {
  const footer = !!hint || !!keys?.length;

  const title = (
    <>
      <Box component="p" sx={{ m: 0, fontSize: '0.72rem', lineHeight: 1.5, color: BODY_TEXT }}>
        {eyebrow && (
          <Box
            component="span"
            sx={{
              mr: 0.75,
              color: eyebrowTone,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: 0.9,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {eyebrow}
          </Box>
        )}
        {body}
      </Box>

      {footer && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            mt: 0.85,
            pt: 0.75,
            borderTop: `1px solid ${BORDER}`,
            color: HINT_TEXT,
            fontSize: '0.63rem',
            lineHeight: 1.4,
          }}
        >
          {keys?.map((k) => <Key key={k}>{k}</Key>)}
          {hint && <Box component="span">{hint}</Box>}
        </Box>
      )}
    </>
  );

  return (
    <Tooltip
      {...rest}
      arrow
      placement={placement}
      title={title}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 300,
            px: 1.25,
            py: 1,
            borderRadius: 1.25,
            bgcolor: SURFACE,
            color: BODY_TEXT,
            border: `1px solid ${BORDER}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
          },
        },
        arrow: { sx: { color: SURFACE, '&::before': { border: `1px solid ${BORDER}` } } },
      }}
    >
      {children}
    </Tooltip>
  );
};

export default RichTooltip;
